/*

  Livability Service

*/
import { DB } from './data-service';

class NbhdModel {
  constructor(nbhd) {
    this.rawData = nbhd;
    this.scale = 100;
  }

  get name() {
    return this.rawData.name;
  }

  get id() {
    return this.rawData.neighbourhoodID;
  }

  // Return a vector that we can use to calculate similarity
  get model() {
    return [
      this.calculateParks() ,
      this.calculateWealth(),
      this.calculateHousingRent(),
      this.calculateHousingOwn(),
      this.calculateFamilyLife(),
      this.calculatePopulation(),
      this.calculateServices(),
      this.calculateTransit(),
      this.calculateCulture(),
      this.calculateDiversity()
    ];
  }

  calculateParks() {
    return this._calculateNormalizedScore(this.rawData.parks.coverage, DB.retrieveParksRange()) * this.scale;
  }

  calculateWealth() {
    const {employed, houseOwners, householdIncome, individualIncome} = DB.retrieveWealthRanges();
    const incomebrackets = DB.discoverIncomeBrackets(this.rawData.income);

    // Calculate Household Income score
    const household_lowIncomeScore = this._calculateNormalizedScore(incomebrackets.households.low, householdIncome.low);
    const household_middleIncomeScore = this._calculateNormalizedScore(incomebrackets.households.middle, householdIncome.middle) * 2;
    const household_highIncomeScore = this._calculateNormalizedScore(incomebrackets.households.high, householdIncome.high) * 4;

    // Calculate Individual Income Score
    const individual_lowIncomeScore = this._calculateNormalizedScore(incomebrackets.individuals.low, individualIncome.low);
    const individual_middleIncomeScore = this._calculateNormalizedScore(incomebrackets.individuals.middle, individualIncome.middle) * 2;
    const individual_highIncomeScore = this._calculateNormalizedScore(incomebrackets.individuals.high, individualIncome.high) * 4;

    const employmentScore = this._calculateNormalizedScore(this.rawData.employment.Employed, employed);
    const houseOwnerScore = this._calculateNormalizedScore(this.rawData.housing.Owner, houseOwners);
    const householdScore = ((household_highIncomeScore + household_middleIncomeScore + household_lowIncomeScore)/7);
    const individualScore = ((individual_highIncomeScore + individual_middleIncomeScore + individual_lowIncomeScore)/7);
    return ((employmentScore + houseOwnerScore + householdScore + individualScore)/4) * this.scale;
  }

  calculateHousingRent() {
    return this._calculateNormalizedScore(this.rawData.housing.Renter, DB.retrieveHousingRentRange()) * this.scale;
  }

  calculateHousingOwn() {
    return this._calculateNormalizedScore(this.rawData.housing.Owner, DB.retrieveHousingOwnRange()) * this.scale;
  }

  calculateFamilyLife() {
    const {childrenPop, elementary, secondary, university, youthServices} = DB.retrieveFamilyLifeRanges();
    const childrenScore = this._calculateNormalizedScore(Number(this.rawData.population['Children (0-14 years)']), childrenPop);
    const elementaryScore = this._calculateNormalizedScore(this.rawData.schools.elementary, elementary);
    const secondaryScore = this._calculateNormalizedScore(this.rawData.schools.secondary, secondary);
    const universityScore = this._calculateNormalizedScore(this.rawData.schools.university, university);
    const youthScore = this._calculateNormalizedScore(this.rawData.youthServices, youthServices);
    return ((childrenScore + elementaryScore + secondaryScore + universityScore + youthScore)/5) * this.scale;
  }

  calculatePopulation() {
    const range = DB.retrievePopulationRange();
    const thisPopulation = this.rawData.population['Population, 2016'];
    return this._calculateNormalizedScore(thisPopulation, range) * this.scale;
  }

  calculateServices() {
    const {fireStations, ambulanceStations, policeStations } = DB.GeneralAttrRanges;
    const fireScore = this._calculateNormalizedScore(this.rawData.fireStations, fireStations);
    const policeScore = this._calculateNormalizedScore(this.rawData.policeStations, policeStations);
    const ambulanceScore = this._calculateNormalizedScore(this.rawData.ambulanceStations, ambulanceStations);
    return ((fireScore + policeScore + ambulanceScore)/3) * this.scale;
  }

  calculateTransit() {
    return this._calculateNormalizedScore(this.rawData.transit.transitMethod['Public transit'], DB.retrieveTransitRanges()) * this.scale;
  }

  calculateCulture() {
    const {languages, culturalSpaces, ethnicOrigins} = DB.retrieveCultureRanges();

    const languageCount = Object.keys(this.rawData.languages.home).filter(language => language !== 'Language spoken most often at home for the total population excluding institutional residents' || language !== 'Official languages').length;
    const ethnicOriginCount = Object.keys(this.rawData.ethnicOrigin).length;

    const languageScore = this._calculateNormalizedScore(languageCount, languages);
    const ethnicScore = this._calculateNormalizedScore(ethnicOriginCount, ethnicOrigins);
    const cultureSpaceScore = this._calculateNormalizedScore(this.rawData.culturalSpaces, culturalSpaces);

    return ((languageScore + ethnicScore + cultureSpaceScore)/3) * this.scale;
  }

  calculateDiversity() {
    const {languages, ethnicOrigins, religion} = DB.retrieveCultureRanges();

    const languageCount = Object.keys(this.rawData.languages.home).filter(language => language !== 'Language spoken most often at home for the total population excluding institutional residents' || language !== 'Official languages').length;
    const ethnicOriginCount = Object.keys(this.rawData.ethnicOrigin).length;
    const religionCount = Object.keys(this.rawData.placesOfWorship).filter(x => this.rawData.placesOfWorship[x] > 0).length;

    const languageScore = this._calculateNormalizedScore(languageCount, languages);
    const ethnicScore = this._calculateNormalizedScore(ethnicOriginCount, ethnicOrigins);
    const religionScore = this._calculateNormalizedScore(religionCount, religion);

    return ((languageScore + ethnicScore + religionScore)/3) * this.scale;
  }

  // Calculate normalized score
  _calculateNormalizedScore(val, rangeObj) {
    if (!val) {
      return 0;
    }
    return (val - rangeObj.min)/(rangeObj.max - rangeObj.min);
  }

  // Check that neighbourhood has relgion
  _matchesReligion(religion) {
    return Object.keys(this.rawData.placesOfWorship).filter(r => this.rawData.placesOfWorship[r] !== 0).map(l => l.toLowerCase()).includes(religion.toLowerCase());
  }

  // Check that religion is most dominant in neighbourhood
  isDominantReligion(religion) {
    if (!this._matchesReligion(religion)) {
      return false;
    }
    const groups = this.rawData.placesOfWorship;
    const values = Object.values(groups);
    const max = Math.max(...values);
    return Object.keys(groups).filter(x => groups[x] === max).includes(religion);
  }

  includesReligion(religion) {
    return Object.keys(this.rawData.placesOfWorship).filter(x => this.rawData.placesOfWorship[x] > 0).map(x => x.toLowerCase()).includes(religion.toLowerCase());
  }


  // Check that the requested language matches the requested language
  _matchesLanguage(language) {
    return Object.keys(this.rawData.languages.native).map(l => l.toLowerCase()).includes(language.toLowerCase());
  }

  // Check if the language is the most dominant or one of the most dominant languages in the neighbourhhod
  isDominantLanguage(language) {
    if (!this._matchesLanguage(language)) {
      return false;
    }
    const langs = this.rawData.languages.native;

    // Remove the general attributes
    langs['Single responses'] = 0;
    langs['Official languages'] = 0;
    langs['Non-Aboriginal languages'] = 0;
    langs['Non-official languages'] = 0;

    const values = Object.values(langs);
    const max = Math.max(...values);

    return Object.keys(langs).filter(x => langs[x] === max).map(x => x.toLowerCase()).includes(language.toLowerCase());
  }

  includesLanguage(language) {
    return Object.keys(this.rawData.languages.native).map(x => x.toLowerCase()).includes(language.toLowerCase());
  }

  // Check if the neighbourhood has the requested ethnicity
  _matchesEthnicity(ethnicity) {
    return Object.keys(this.rawData.ethnicOrigin).map(l => l.toLowerCase()).includes(ethnicity.toLowerCase());
  }

  // Check that ethnicity is the most dominant in neighbourhood
  isDominantEthnicity(ethnicity) {
    if (!this._matchesEthnicity(ethnicity)) {
      return false;
    }
    const groups = this.rawData.ethnicOrigin;
    const values = Object.values(groups);
    const max = Math.max(...values);
    return Object.keys(groups).filter(x => groups[x] === max).includes(ethnicity);
  }

  includesEthnicity(ethnicity) {
    return Object.keys(this.rawData.ethnicOrigin).filter(x => this.rawData.ethnicOrigin[x] > 0).map(x => x.toLowerCase()).includes(ethnicity.toLowerCase());
  }
}

const similarity = require('compute-cosine-similarity');
class LivabilityService {
  constructor() {
    this.neighbourhoods = DB.neighbourhoods.map(n => new NbhdModel(n));
  }

  /**
   * Calculate Livability
   *
   * Calculate the livability scores for each neighbourhood in toronto compared to the user input.
   *
   * @param {Array<Number>} userModel An array of 10 Numbers which represent the user's preferences. [parks, wealth, house renters, house owners, family life, population, services, transit, culture, diversity ]
   * @param {{religion: string, ethnicity: string, language: string}} diversity Filter neighbourhoods by specific characteristics
   * @param limit Specify the number of elements to return, if you want all enter 0
  */
  calculateLivability(userModel, diversity = null, limit = 5) {
    if (!Array.isArray(userModel) || !userModel.length === 10 || (Math.max(...userModel) > 10) || (Math.min(...userModel) < 0)) {
      return;
    }

    let results = {
      couldFilterByDiversity: true,
      neighbourhoods: null
    };

    // Convert to NbhdModel scale: 100
    userModel = userModel.map(val => val*10);
    let nbhdVectors = diversity ? this.neighbourhoods.filter(n => {
      let religion = true;
      let ethnicity = true;
      let language = true;

      if (diversity.religion) {
        religion = n.includesReligion(diversity.religion.value);
      }

      if (diversity.ethnicity) {
        ethnicity = n.includesEthnicity(diversity.ethnicity.value);
      }

      if (diversity.language) {
        language = n.includesLanguage(diversity.language.value);
      }

      return religion && ethnicity && language;

    }).map(n => {
      return {
        id: n.id,
        name: n.name,
        similarity: similarity(userModel, n.model)
      }
    }) : this.neighbourhoods.map(n => {
      return {
        id: n.id,
        name: n.name,
        similarity: similarity(userModel, n.model)
      }
    });

    // If Diversity is requested and there are no avaliable neighbourhoods set to the original neighbourhoods without filter
    if (diversity && nbhdVectors.length === 0) {
      results.couldFilterByDiversity = false;
      nbhdVectors = this.neighbourhoods.map(n => {
        return {
          id: n.id,
          name: n.name,
          similarity: similarity(userModel, n.model)
        }
      });
    }


    // Limit the results
    if (limit === 0 || limit >= 140 || limit < 0 || typeof limit !== 'number') {
      return {
        couldFilterByDiversity: results.couldFilterByDiversity,
        neighbourhoods: nbhdVectors.sort((a,b) => b.similarity-a.similarity)
      };
    }
    return {
      couldFilterByDiversity: results.couldFilterByDiversity,
      neighbourhoods: nbhdVectors.sort((a,b) => b.similarity-a.similarity).slice(0, limit)
    };

  }
}

const LS = new LivabilityService();

export { LS };
